import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { DeviceMobile, Lock, ArrowRight, ShieldCheck, Spinner, Sparkle, Buildings, Lightning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatApiError } from '../lib/api';
import PWAInstallBanner from '../components/PWAInstallBanner';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const APP_LOGO = '/brand/cut2pro-logo.png';
const APP_NAME = 'Cut2Pro';
const APP_TAGLINE = 'Cut Smart. Track Smart. Produce Perfect.';

const BG_IMAGES = [
  'https://images.unsplash.com/photo-1654053628448-3aa806f6932c?w=1200&q=80',
  'https://images.unsplash.com/photo-1600662038794-c577b200e81f?w=1200&q=80',
  'https://images.unsplash.com/photo-1610048170798-85c540492ce4?w=1200&q=80',
];

const slideVariants = {
  enter: { x: 60, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -60, opacity: 0 },
};

function PinInput({ value, onChange, length = 4, autoFocus = false }) {
  const refs = useRef([]);
  const handleChange = (idx, val) => {
    if (val.length > 1) val = val[val.length - 1];
    if (val && !/^\d$/.test(val)) return;
    const arr = value.split('');
    arr[idx] = val;
    const newVal = arr.join('').slice(0, length);
    onChange(newVal);
    if (val && idx < length - 1) refs.current[idx + 1]?.focus();
  };
  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };
  return (
    <div className="flex gap-3 justify-center">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          autoFocus={autoFocus && i === 0}
          data-testid={`pin-digit-${i}`}
          className="w-14 h-16 text-center text-3xl font-bold rounded-xl border-2 border-stone-300 bg-white text-[#2B2628] focus:border-[#6B4D5F] focus:ring-2 focus:ring-[#6B4D5F]/20 outline-none transition-all shadow-sm"
        />
      ))}
    </div>
  );
}

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // phone | pin | verify_default | set_new
  const [phone, setPhone] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return (sp.get('phone') || '').replace(/\D/g, '').slice(0, 10);
    } catch { return ''; }
  });
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [defaultPinVerified, setDefaultPinVerified] = useState(false);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [showEntry, setShowEntry] = useState(false);

  useEffect(() => { if (user && !showEntry) navigate('/dashboard', { replace: true }); }, [user, navigate, showEntry]);

  // Force light scheme on auth pages (dark mode otherwise makes the white card text invisible)
  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains('dark');
    if (wasDark) html.classList.remove('dark');
    return () => { if (wasDark) html.classList.add('dark'); };
  }, []);

  // Re-apply: ThemeProvider may toggle .dark back when its effectiveMode flips.
  // We watch the html node and strip `.dark` for as long as we're mounted.
  useEffect(() => {
    const html = document.documentElement;
    const obs = new MutationObserver(() => {
      if (html.classList.contains('dark')) html.classList.remove('dark');
    });
    obs.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Cycle background images
  useEffect(() => {
    const t = setInterval(() => setBgIndex(p => (p + 1) % BG_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/phone-check', { phone });
      setUserName(res.data.name || '');
      if (!res.data.has_pin || res.data.must_change_pin) {
        setStep('verify_default');
      } else {
        setStep('pin');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || err.message || 'Connection error. Please try again.');
    } finally { setLoading(false); }
  };

  const handlePinLogin = async () => {
    if (pin.length !== 4) return;
    setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/phone-login', { phone, pin });
      triggerEntry(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || err.message || 'Connection error. Please try again.');
      setPin('');
    } finally { setLoading(false); }
  };

  const handleSetPin = async () => {
    if (newPin.length !== 4) { setError('Enter a 4-digit PIN'); return; }
    if (newPin !== confirmPin) { setError('PINs do not match'); return; }
    setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/set-pin', { phone, current_pin: pin, new_pin: newPin });
      triggerEntry(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || err.message || 'Connection error. Please try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyDefault = async () => {
    if (pin.length !== 4) return;
    setError(''); setLoading(true);
    try {
      // Verify the default PIN works by attempting a phone-login
      await api.post('/auth/phone-login', { phone, pin });
      // Default PIN is correct, move to set new PIN
      setDefaultPinVerified(true);
      setStep('set_new');
      setError('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Wrong default PIN. Ask your admin.');
      setPin('');
    } finally { setLoading(false); }
  };

  const triggerEntry = (userData) => {
    setShowEntry(true);
    setTimeout(() => {
      setUser(userData);
      setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
    }, 1800);
  };

  // Auto-submit when PIN is complete
  useEffect(() => {
    if (step === 'pin' && pin.length === 4 && !loading) handlePinLogin();
    if (step === 'verify_default' && pin.length === 4 && !loading) handleVerifyDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  // Animated entry overlay
  if (showEntry) {
    return (
      <motion.div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-br from-[#0E1B2C] via-[#15263D] to-[#1E3A5F]"
        initial={{ clipPath: 'circle(0% at 50% 100%)' }}
        animate={{ clipPath: 'circle(150% at 50% 100%)' }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="relative inline-block mb-5">
            <div aria-hidden className="absolute inset-0 -m-4 rounded-3xl bg-emerald-400/25 blur-2xl animate-pulse" />
            <img
              src={APP_LOGO}
              alt={APP_NAME}
              className="relative h-24 sm:h-28 w-auto object-contain rounded-2xl shadow-[0_8px_40px_rgba(0,201,167,0.55)] ring-1 ring-emerald-300/30"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl font-light text-white tracking-tight" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Welcome{userName ? `, ${userName.length > 24 ? userName.split(' ')[0] : userName}` : ''}
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="text-emerald-300/80 text-sm mt-3 tracking-widest uppercase"
            style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}
          >
            {APP_NAME}
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div className="auth-light-scope min-h-screen flex flex-col relative overflow-hidden">
      <PWAInstallBanner />
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={bgIndex}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            <img src={BG_IMAGES[bgIndex]} alt="" className="w-full h-full object-cover" />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B2628]/90 via-[#2B2628]/50 to-[#6B4D5F]/30" />
        <div className="absolute inset-0 backdrop-blur-[2px]" />
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-5 sm:px-6 py-6 sm:py-8 w-full max-w-full">
        {/* Branding — permanent Cut2Pro identity */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-5 sm:mb-7 w-full max-w-md mx-auto"
        >
          <div className="relative inline-block mb-3 sm:mb-4">
            {/* Soft halo glow */}
            <div aria-hidden className="absolute inset-0 -m-3 rounded-3xl bg-emerald-400/20 blur-2xl" />
            <img
              src={APP_LOGO}
              alt={APP_NAME}
              className="relative h-20 sm:h-28 w-auto object-contain rounded-2xl shadow-[0_8px_32px_rgba(0,201,167,0.45)] ring-1 ring-emerald-300/30"
              data-testid="app-logo"
            />
          </div>
          {/* Animated tagline pill — readable on any background, fits any width */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="inline-flex items-center max-w-full"
            data-testid="app-tagline"
          >
            <div className="relative tagline-shimmer-bg backdrop-blur-md rounded-full px-4 sm:px-5 py-1.5 sm:py-2 ring-1 ring-emerald-300/40 shadow-[0_4px_24px_-4px_rgba(16,185,129,0.45)]">
              <p
                className="tagline-text-glow text-emerald-200 text-[9px] sm:text-[12px] font-bold tracking-[0.18em] sm:tracking-[0.24em] uppercase whitespace-nowrap"
                style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}
              >
                {APP_TAGLINE}
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Glass Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="w-full max-w-sm mx-auto"
        >
          <div className="bg-white rounded-3xl shadow-[0_24px_70px_-12px_rgba(0,0,0,0.55)] border border-white/40 p-6 sm:p-8 ring-1 ring-stone-200/50" data-testid="login-card">
            <AnimatePresence mode="wait">
              {/* STEP: PHONE */}
              {step === 'phone' && (
                <motion.div key="phone" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                  <div className="flex items-center gap-3 mb-7">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#6B4D5F] to-[#5A3D4E] flex items-center justify-center shadow-md">
                      <DeviceMobile size={24} className="text-white" weight="duotone" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-[#2B2628] leading-none" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>Sign In</h2>
                      <p className="text-[13px] text-[#5A4F54] font-medium mt-1">Enter your registered mobile number</p>
                    </div>
                  </div>
                  <form onSubmit={handlePhoneSubmit}>
                    {error && <div data-testid="login-error" className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">{error}</div>}
                    <div className="mb-6">
                      <label className="text-[11px] font-bold text-[#3A3338] uppercase tracking-[0.18em] mb-2 block">Mobile Number</label>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-[#6B4D5F] bg-[#6B4D5F]/8 px-3.5 py-3 rounded-xl border border-[#E8C7C8]/60">+91</span>
                        <Input
                          data-testid="phone-input"
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={phone}
                          onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setError(''); }}
                          placeholder="Enter 10 digit number"
                          className="h-12 text-lg font-semibold text-[#2B2628] tracking-wider bg-white border-2 border-stone-200 focus:border-[#6B4D5F] placeholder:text-stone-400 placeholder:font-normal placeholder:text-base"
                          autoFocus
                        />
                      </div>
                    </div>
                    <Button
                      data-testid="submit-phone-btn"
                      type="submit"
                      disabled={phone.length < 10 || loading}
                      className="w-full h-13 py-3.5 bg-gradient-to-r from-[#6B4D5F] to-[#2B2628] hover:from-[#5A3D4E] hover:to-[#1F1A1C] text-white text-base font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-[#6B4D5F]/40 disabled:opacity-50"
                    >
                      {loading ? <Spinner size={20} className="animate-spin" /> : <><span>Continue</span> <ArrowRight size={18} className="ml-2" weight="bold" /></>}
                    </Button>
                  </form>
                  <div className="mt-6 pt-4 border-t border-stone-200 text-center">
                    <p className="text-[12px] text-[#5A4F54] font-medium">
                      Already a member? Just enter your number above.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* STEP: PIN LOGIN */}
              {step === 'pin' && (
                <motion.div key="pin" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6B4D5F] to-[#2B2628] flex items-center justify-center mx-auto mb-3 shadow-md">
                      <Lock size={26} className="text-white" weight="duotone" />
                    </div>
                    {userName && <p className="text-lg font-bold text-[#2B2628] mb-0.5">Hi, {userName.split(' ')[0]}</p>}
                    <p className="text-[13px] text-[#5A4F54] font-medium">Enter your 4-digit PIN</p>
                  </div>
                  {error && <div data-testid="login-error" className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-center">{error}</div>}
                  <div className="mb-6">
                    <PinInput value={pin} onChange={setPin} autoFocus />
                  </div>
                  <Button
                    data-testid="login-btn"
                    onClick={handlePinLogin}
                    disabled={pin.length < 4 || loading}
                    className="w-full h-13 py-3.5 bg-gradient-to-r from-[#6B4D5F] to-[#2B2628] hover:from-[#5A3D4E] hover:to-[#1F1A1C] text-white text-base font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-[#6B4D5F]/40 disabled:opacity-50"
                  >
                    {loading ? <Spinner size={20} className="animate-spin" /> : <><ShieldCheck size={20} className="mr-2" weight="bold" /> Unlock</>}
                  </Button>
                  <div className="flex items-center justify-between mt-5">
                    <button onClick={() => { setStep('phone'); setPin(''); setError(''); }} className="text-[12px] font-semibold text-[#5A4F54] hover:text-[#6B4D5F] transition-colors" data-testid="change-number-btn">
                      Change number
                    </button>
                    <button onClick={() => { setStep('verify_default'); setPin(''); setError(''); }} className="text-[12px] text-[#6B4D5F] font-bold hover:underline" data-testid="forgot-pin-btn">
                      Forgot PIN?
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP: VERIFY DEFAULT PIN */}
              {step === 'verify_default' && (
                <motion.div key="verify_default" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-amber-100 border-2 border-amber-200 flex items-center justify-center mx-auto mb-3">
                      <Lock size={26} className="text-amber-700" weight="duotone" />
                    </div>
                    {userName && <p className="text-lg font-bold text-[#2B2628] mb-0.5">Hi, {userName.split(' ')[0]}</p>}
                    <p className="text-[13px] text-[#5A4F54] font-medium">Enter the default PIN given by your admin</p>
                  </div>
                  {error && <div data-testid="login-error" className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-center">{error}</div>}
                  <div className="mb-6">
                    <PinInput value={pin} onChange={setPin} autoFocus />
                  </div>
                  <Button
                    data-testid="verify-default-btn"
                    onClick={handleVerifyDefault}
                    disabled={pin.length < 4 || loading}
                    className="w-full h-13 py-3.5 bg-gradient-to-r from-[#6B4D5F] to-[#2B2628] hover:from-[#5A3D4E] hover:to-[#1F1A1C] text-white text-base font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-[#6B4D5F]/40 disabled:opacity-50"
                  >
                    {loading ? <Spinner size={20} className="animate-spin" /> : <><span>Verify</span> <ArrowRight size={18} className="ml-2" weight="bold" /></>}
                  </Button>
                  <button onClick={() => { setStep('phone'); setPin(''); setError(''); }} className="w-full text-[12px] font-semibold text-[#5A4F54] hover:text-[#6B4D5F] mt-5 transition-colors" data-testid="back-btn">
                    Back to mobile number
                  </button>
                </motion.div>
              )}

              {/* STEP: SET NEW PIN (after default verified) */}
              {step === 'set_new' && (
                <motion.div key="set_new" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                  <div className="text-center mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-3">
                      <ShieldCheck size={26} className="text-emerald-700" weight="duotone" />
                    </div>
                    <h2 className="text-lg font-bold text-[#2B2628]">Choose Your PIN</h2>
                    <p className="text-[13px] text-[#5A4F54] font-medium mt-1">Set a new 4-digit PIN for future logins</p>
                  </div>
                  {error && <div data-testid="login-error" className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-center">{error}</div>}
                  <div className="space-y-5">
                    <div>
                      <label className="text-[11px] font-bold text-[#3A3338] uppercase tracking-[0.18em] mb-2 block">New PIN</label>
                      <PinInput value={newPin} onChange={setNewPin} autoFocus />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-[#3A3338] uppercase tracking-[0.18em] mb-2 block">Confirm PIN</label>
                      <PinInput value={confirmPin} onChange={setConfirmPin} />
                    </div>
                  </div>
                  <Button
                    data-testid="set-pin-btn"
                    onClick={handleSetPin}
                    disabled={newPin.length < 4 || confirmPin.length < 4 || loading}
                    className="w-full h-13 py-3.5 bg-gradient-to-r from-[#6B4D5F] to-[#2B2628] hover:from-[#5A3D4E] hover:to-[#1F1A1C] text-white text-base font-bold rounded-2xl mt-6 transition-all duration-300 shadow-lg shadow-[#6B4D5F]/40 disabled:opacity-50"
                  >
                    {loading ? <Spinner size={20} className="animate-spin" /> : 'Set PIN & Continue'}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Create-Account CTA — prominent, conversion-focused */}
          {step === 'phone' && (
            <motion.a
              href="/signup"
              data-testid="signup-cta-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              whileHover={{ scale: 1.015, y: -2 }}
              whileTap={{ scale: 0.99 }}
              className="block mt-5 group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-3xl border border-emerald-300/40 bg-gradient-to-br from-emerald-500/95 via-teal-500/95 to-cyan-500/95 backdrop-blur-xl p-5 shadow-[0_12px_40px_-8px_rgba(16,185,129,0.55)] transition-all duration-300 group-hover:shadow-[0_20px_60px_-10px_rgba(16,185,129,0.7)]">
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-1000 ease-out" />
                <div className="relative flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0 ring-1 ring-white/30">
                    <Buildings size={22} className="text-white" weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-white/95 bg-white/15 px-2 py-0.5 rounded-full ring-1 ring-white/25">
                        New here?
                      </span>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-50 inline-flex items-center gap-1">
                        <Sparkle size={10} weight="fill" /> 15 days free
                      </span>
                    </div>
                    <p className="text-[15px] font-bold text-white leading-tight" style={{ fontFamily: 'Outfit, Manrope, sans-serif' }}>
                      Start your factory's free trial
                    </p>
                    <p className="text-[11px] text-white/85 leading-snug mt-0.5">
                      No credit card · Setup in 2 minutes
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-white text-emerald-600 flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:translate-x-1">
                    <ArrowRight size={18} weight="bold" />
                  </div>
                </div>
                <div className="relative grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-white/20">
                  {[
                    { icon: Lightning, label: 'Multi-factory' },
                    { icon: ShieldCheck, label: 'Secure & private' },
                    { icon: Sparkle, label: 'Cancel anytime' },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-1.5 text-[9px] sm:text-[10px] text-white/90 font-semibold uppercase tracking-wider">
                      <Icon size={11} weight="fill" /> {label}
                    </div>
                  ))}
                </div>
              </div>
            </motion.a>
          )}

          {/* Footer */}
          <p className="text-center text-white/30 text-[10px] mt-6 tracking-wider uppercase">
            Secure access for registered users only
          </p>
        </motion.div>
      </div>
    </div>
  );
}
